import React, { useState, useEffect } from 'react';
import { getListings } from '../../service/Listings/getListings';
 import { getCurrentConfig } from '../../config/config';
 import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Chip,
  Button,
  Pagination,
  Skeleton,
  Divider,
  Avatar,
  Stack,
  Paper,
  Fade,
  Tooltip,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Search,
  Phone,
  Sms,
  Email,
  Bed,
  Bathtub,
  DirectionsCar,
  Clear,
  Person,
  Home,
  LocationOn,
  Add
} from '@mui/icons-material';
 
import { useNavigate,Link } from 'react-router-dom';

const ListingView = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const config = getCurrentConfig();
  const navigate = useNavigate();
   const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProperties.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProperties.length / itemsPerPage);

  // Fetch properties function
  const fetchProperties = async () => {
    setIsLoading(true);
    try {
      const response = await getListings();
      const fetchedProperties = response.data;
      setProperties(fetchedProperties);
      setFilteredProperties(fetchedProperties);
    } catch (error) {
     

    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    fetchProperties();
  }, []);


  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1); 
    
    if (query === '') {
      setFilteredProperties(properties);
    } else if (query?.length >= 4) {
      const searchTerm = query.toLowerCase();
      const filtered = properties.filter(item =>
        item.streetName?.toLowerCase().includes(searchTerm) ||
        item.suburb?.toLowerCase().includes(searchTerm) ||
        item.streetNumber?.toString().toLowerCase().includes(searchTerm) ||
        item.vendorFirstName?.toLowerCase().includes(searchTerm) ||
        item.vendorLastName?.toLowerCase().includes(searchTerm) ||
        `${item.vendorFirstName} ${item.vendorLastName}`.toLowerCase().includes(searchTerm) ||
        `${item.streetNumber} ${item.streetName} ${item.suburb}`.toLowerCase().includes(searchTerm)
      );
      setFilteredProperties(filtered);
    }
  };

  const handleCall = (phoneNumber) => {
    window.open(`tel:${phoneNumber}`, '_self');
  };

  const handleSMS = (phoneNumber) => {
    window.open(`sms:${phoneNumber}`, '_self');
  };

  const handleEmail = (email) => {
    if (email) {
      const emailUri = `mailto:${email}?subject=Hello&body=Hi`;
      window.open(emailUri, '_self');
    }
  };
  
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  const handlePropertySelect = (item) => {
 
       navigate(`/listings/${item.propertyId}/listingedit`, { 
        state: { propertyId: item.propertyId } 
      });
  };

  const clearSearch = () => {
    setSearchQuery('');
    setFilteredProperties(properties);
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const SkeletonLoader = () => (
    <Grid container spacing={3}>
      {[...Array(6)].map((_, index) => (
        <Grid  md={4} key={index}>
          <Card sx={{ height: '100%' }}>
            <Skeleton variant="rectangular" height={200} />
            <CardContent>
              <Skeleton variant="text" height={32} />
              <Skeleton variant="text" height={24} width="60%" />
              <Box sx={{ display: 'flex', gap: 2, my: 2 }}>
                <Skeleton variant="circular" width={40} height={40} />
                <Skeleton variant="circular" width={40} height={40} />
                <Skeleton variant="circular" width={40} height={40} />
              </Box>
              <Skeleton variant="text" height={24} />
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Skeleton variant="circular" width={32} height={32} />
                <Skeleton variant="circular" width={32} height={32} />
                <Skeleton variant="circular" width={32} height={32} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const Pagination = () => {
    const pageNumbers = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="pagination">
        <button
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
          className="pagination-btn pagination-prev"
        >
          Previous
        </button>
        
        {startPage > 1 && (
          <>
            <button
              onClick={() => goToPage(1)}
              className="pagination-btn"
            >
              1
            </button>
            {startPage > 2 && <span className="pagination-ellipsis">...</span>}
          </>
        )}
        
        {pageNumbers.map(number => (
          <button
            key={number}
            onClick={() => goToPage(number)}
            className={`pagination-btn ${currentPage === number ? 'pagination-active' : ''}`}
          >
            {number}
          </button>
        ))}
        
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="pagination-ellipsis">...</span>}
            <button
              onClick={() => goToPage(totalPages)}
              className="pagination-btn"
            >
              {totalPages}
            </button>
          </>
        )}
        
        <button
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="pagination-btn pagination-next"
        >
          Next
        </button>
      </div>
    );
  };

  const PropertyCard = ({ item }) => (
    <Fade in timeout={300}>
      <Card 
        sx={{ 
          height: '100%', 
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6
          }
        }}
        onClick={() => handlePropertySelect(item)}
      >
        <CardMedia
          component="img"
          height="200"
          image={item.propertyPhoto === '' ? config.noListingImage : item.propertyPhoto}
          alt={`${item.streetNumber} ${item.streetName}`}
          sx={{
            objectFit: 'cover',
            borderRadius: '4px 4px 0 0'
          }}
        />
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Home sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
            <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
              {item.streetNumber} {item.streetName}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <LocationOn sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
            <Typography variant="body2" color="text.secondary">
              {item.suburb}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Chip
              icon={<Bed />}
              label={item.bedrooms}
              size="small"
              variant="outlined"
              color="primary"
            />
            <Chip
              icon={<Bathtub />}
              label={item.bathrooms}
              size="small"
              variant="outlined"
              color="primary"
            />
            <Chip
              icon={<DirectionsCar />}
              label={(item.carSpaces || 0) + (item.garages || 0) + (item.carPorts || 0)}
              size="small"
              variant="outlined"
              color="primary"
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: 'primary.main' }}>
                <Person fontSize="small" />
              </Avatar>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {item.vendorFirstName?.trim()} {item.vendorLastName?.trim()}
              </Typography>
            </Box>

            <Stack direction="row" spacing={0.5}>
              {item.mobile && (
                <>
                  <Tooltip title="Call">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCall(item.mobile);
                      }}
                      sx={{ 
                        color: 'success.main',
                        '&:hover': { bgcolor: 'success.light', color: 'white' }
                      }}
                    >
                      <Phone fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="SMS">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSMS(item.mobile);
                      }}
                      sx={{ 
                        color: 'info.main',
                        '&:hover': { bgcolor: 'info.light', color: 'white' }
                      }}
                    >
                      <Sms fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              {item.email && (
                <Tooltip title="Email">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEmail(item.email);
                    }}
                    sx={{ 
                      color: 'warning.main',
                      '&:hover': { bgcolor: 'warning.light', color: 'white' }
                    }}
                  >
                    <Email fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Fade>
  );

  return (
    <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        <Paper elevation={0} sx={{ p: 4, borderRadius: 2 }}>
          <Box display="flex" justifyContent="flex-end" mb={3}>
            <Button
              component={Link}
              to="/listings/add"
              variant="contained"
              startIcon={<Add />}
              sx={{ minWidth: isMobile ? 'auto' : 140 }}
            >
              {isMobile ? 'Add' : 'Add Listing'}
            </Button>
          </Box>
          {/* Search Bar */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search properties (address, suburb, vendor)"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              slotProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery.length >= 1 && (
                  <InputAdornment position="end">
                    <IconButton onClick={clearSearch} size="small">
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  bgcolor: 'white'
                }
              }}
            />
          </Box>

          {/* Results Summary */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredProperties.length)} of {filteredProperties.length} properties
            </Typography>
            {filteredProperties.length > 0 && (
              <Chip 
                label={`${filteredProperties.length} Results`}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
          </Box>

          {/* Property List */}
          {isLoading ? (
            <SkeletonLoader />
          ) : filteredProperties.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Alert severity="info" sx={{ maxWidth: 400, mx: 'auto' }}>
                <Typography variant="h6" gutterBottom>
                  No Listings Found
                </Typography>
                <Typography variant="body2">
                  Try adjusting your search criteria
                </Typography>
              </Alert>
            </Box>
          ) : (
            <>
              <Grid container spacing={3}>
                {currentItems.map((item) => (
                  <Grid  md={4} key={item.propertyId}>
                    <PropertyCard item={item} />
                  </Grid>
                ))}
              </Grid>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                    sx={{
                      '& .MuiPaginationItem-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default ListingView;